import {
  Button,
  createStyles,
  Grid,
  Paper,
  Typography,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import { Trans, useTranslations } from "adapters/i18n";
import { Show } from "features/shared/components";
import { useConfirmDialog } from "features/shared/hooks/useConfirmDialog";
import React, { useState } from "react";
import {
  createHerdRecord,
  deleteHerdRecord,
  updateHerdRecord,
} from "redux/operations";
import {
  EnrichedAnimalCategory,
  EnrichedHerdRecord,
  OperationType,
} from "shared/types";
import { MovementFormState } from "../movements.types";
import { parseCarcassYield, WARNING_ICON_PATH } from "../utils/movements.utils";
import MovementsForm from "./movementsForm";
import MovementsListTable from "./movementsListTable";

type MovementsPageProps = {
  createHerdRecord: (
    ...params: Parameters<typeof createHerdRecord>
  ) => Promise<void>;
  deleteHerdRecord: (
    ...params: Parameters<typeof deleteHerdRecord>
  ) => Promise<void>;
  enrichedFarmCategories: EnrichedAnimalCategory[];
  farmEnrichedHerdRecords: EnrichedHerdRecord[];
  operationTypes: OperationType[];
  updateHerdRecord: (
    ...params: Parameters<typeof updateHerdRecord>
  ) => Promise<void>;
} & WithStyles<typeof styles>;

function MovementsPage({
  enrichedFarmCategories: categories,
  classes,
  createHerdRecord,
  deleteHerdRecord,
  farmEnrichedHerdRecords: movements,
  operationTypes,
  updateHerdRecord,
}: MovementsPageProps) {
  const { t } = useTranslations();

  const [isFormPanelOpen, setIsFormPanelOpen] = useState<boolean>(false);
  const [selectedMovement, setSelectedMovement] =
    useState<EnrichedHerdRecord | null>(null);
  const [targetDeleteMovement, setTargetDeleteMovement] =
    useState<EnrichedHerdRecord | null>(null);

  const handleCloseModal = () => {
    setTargetDeleteMovement(null);
  };

  const handleConfirmDelete = () => {
    if (targetDeleteMovement == null) {
      return;
    }

    deleteHerdRecord(targetDeleteMovement.id);
    handleCloseModal();
  };

  const { render: DeleteMovementDialog, setVisible } = useConfirmDialog({
    cancelButtonText: t("common:actions.cancel"),
    confirmButtonText: t("common:actions.yes"),
    onCancel: handleCloseModal,
    onConfirm: handleConfirmDelete,
    title: t("movements:delete-movement-modal.title"),
    text: t("movements:delete-movement-modal.content"),
  });

  const toggleFormPanel = (isOpen: boolean) => {
    setIsFormPanelOpen(isOpen);
    if (isOpen) {
      return;
    }
    setSelectedMovement(null);
  };

  const handleCloseFormPanel = () => {
    toggleFormPanel(false);
  };

  const handleOpenNewMovementPanel = () => {
    setSelectedMovement(null);
    toggleFormPanel(true);
  };

  const handleSubmitForm = async (formState: MovementFormState) => {
    const carcassYield = parseCarcassYield(
      formState.carcassYield.value
    ) as number;
    if (selectedMovement) {
      await updateHerdRecord(selectedMovement.id, {
        animalCategoryId: formState.animalCategoryId.value as number,
        carcassYield,
        operationDate: formState.operationDate.value as string,
        operationTypeId: formState.operationTypeId.value as number,
        quantity: formState.quantity.value as number,
        recordTypeId: formState.recordTypeId.value as number,
        weight: formState.weight.value as number,
      });
      toggleFormPanel(false);
      return;
    }

    await createHerdRecord({
      animalCategoryId: formState.animalCategoryId.value as number,
      carcassYield,
      operationDate: formState.operationDate.value as string,
      operationTypeId: formState.operationTypeId.value as number,
      quantity: formState.quantity.value as number,
      recordTypeId: formState.recordTypeId.value as number,
      weight: formState.weight.value as number,
      confirmed: true,
    });

    toggleFormPanel(false);
  };

  const handleEditMovement = (row: EnrichedHerdRecord) => {
    setSelectedMovement(row);
    toggleFormPanel(true);
  };

  const handleConfirmMovement = async (row: EnrichedHerdRecord) => {
    await updateHerdRecord(row.id, { confirmed: true });
  };

  const handleDeleteRequest = (row: EnrichedHerdRecord) => {
    setTargetDeleteMovement(row);
    setVisible(true);
  };

  return (
    <Paper className={classes.root} square={true}>
      <Grid item sm={12} xs={12} className={classes.grid}>
        <Typography className={classes.header} gutterBottom={true}>
          {t("movements:entries-and-exits-records")}
        </Typography>

        <Typography className={classes.warningText}>
          <img
            src={WARNING_ICON_PATH}
            alt={t("common:warning-icon")}
            className={classes.warningIcon}
          />
          <Trans
            i18nKey="movements:portal-vs-app-disclaimer"
            components={{ bold: <strong /> }}
          />
        </Typography>

        <Show
          when={isFormPanelOpen}
          fallback={
            <AddNewButton
              classes={classes}
              onClick={handleOpenNewMovementPanel}
            />
          }
        >
          <MovementsForm
            categories={categories}
            onCancel={handleCloseFormPanel}
            onSubmit={handleSubmitForm}
            operationTypes={operationTypes}
            selectedHerdRecord={selectedMovement}
          />
        </Show>

        <MovementsListTable
          isFormPanelOpen={isFormPanelOpen}
          movements={movements}
          onConfirmRequest={handleConfirmMovement}
          onDeleteRequest={handleDeleteRequest}
          onEditClick={handleEditMovement}
        />

        <DeleteMovementDialog />
      </Grid>
    </Paper>
  );
}

type AddNewButtonProps = {
  onClick: () => void;
} & WithStyles<typeof styles>;

function AddNewButton({ classes, onClick }: AddNewButtonProps) {
  const { t } = useTranslations();

  return (
    <Button
      className={classes.addNewButton}
      color="primary"
      data-testid="add-new-movement-button"
      onClick={onClick}
      variant="contained"
    >
      {t("common:actions.create-new")}
    </Button>
  );
}

const styles = () =>
  createStyles({
    root: {
      bottom: 0,
      height: "100%",
      minHeight: "calc(100vh - 90px)",
      overflowX: "auto",
      padding: "46px",
      top: "17%",
      width: "100%",
      zIndex: -1,
    },
    grid: {
      backgroundColor: "#fff",
      flexGrow: 1,
      height: "100%",
      width: "100%",
    },
    header: {
      fontSize: "20px",
      fontWeight: "bold",
      fontStyle: "medium",
      fontStretch: "medium",
      lineHeight: "medium",
      letterSpacing: "medium",
      color: "#333333",
      marginTop: "40px",
    },
    addNewButton: {
      borderRadius: "18px",
      float: "right",
      marginBottom: "16px",
      marginRight: 0,
      marginTop: "34px",
      width: "153px",
    },
    warningIcon: {
      marginRight: "4px",
      marginBottom: "-3px",
      height: "20px",
      color: "red",
    },
    warningText: {
      fontFamily: "Roboto",
      lineHeight: 1.71,
      color: "#666",
      fontSize: "0.9rem",
      marginTop: "4vh",
    },
  });

export default withStyles(styles)(MovementsPage);
